import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * PasswordStrengthIndicator Component
 *
 * Visual indicator for password strength with requirements checklist.
 * Provides real-time feedback on password quality.
 *
 * Features:
 * - Visual strength bar (weak/medium/strong)
 * - Color-coded feedback
 * - Requirements checklist with checkmarks
 * - Accessibility support
 */
function PasswordStrengthIndicator({ password, requirements, strength }) {
  const { t } = useTranslation('profile')

  // Calculate strength level and properties
  const strengthProps = useMemo(() => {
    if (strength <= 2) {
      return {
        level: 'weak',
        label: t('profile:security.strength.weak'),
        color: 'red',
        width: '33%'
      }
    } else if (strength <= 3) {
      return {
        level: 'medium',
        label: t('profile:security.strength.medium'),
        color: 'amber',
        width: '66%'
      }
    } else {
      return {
        level: 'strong',
        label: t('profile:security.strength.strong'),
        color: 'green',
        width: '100%'
      }
    }
  }, [strength, t])

  // Color classes for different strength levels
  const colorClasses = {
    red: {
      bg: 'bg-red-500',
      text: 'text-red-700',
      border: 'border-red-200',
      light: 'bg-red-50'
    },
    amber: {
      bg: 'bg-amber-500',
      text: 'text-amber-700',
      border: 'border-amber-200',
      light: 'bg-amber-50'
    },
    green: {
      bg: 'bg-green-500',
      text: 'text-green-700',
      border: 'border-green-200',
      light: 'bg-green-50'
    }
  }

  const currentColor = colorClasses[strengthProps.color]

  return (
    <div className="space-y-3">
      {/* Strength Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            {t('profile:security.strength.label')}
          </span>
          <span className={`text-sm font-medium ${currentColor.text}`}>
            {strengthProps.label}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${currentColor.bg} transition-all duration-300 ease-out rounded-full`}
            style={{ width: strengthProps.width }}
            role="progressbar"
            aria-valuenow={strength}
            aria-valuemin="0"
            aria-valuemax="5"
            aria-label={t('profile:security.strength.aria', { strength: strengthProps.label })}
          />
        </div>
      </div>

      {/* Requirements Checklist */}
      <div className={`border rounded-lg p-3 ${currentColor.border} ${currentColor.light}`}>
        <p className="text-xs font-medium text-gray-700 mb-2">
          {t('profile:security.requirements.title')}
        </p>
        <ul className="space-y-1" role="list">
          {requirements.map((req) => {
            const isMet = req.test(password)
            return (
              <li
                key={req.id}
                className={`flex items-center text-xs ${
                  isMet ? 'text-green-700' : 'text-gray-500'
                }`}
                role="listitem"
              >
                <span
                  className={`mr-2 flex-shrink-0 ${
                    isMet ? 'text-green-600' : 'text-gray-400'
                  }`}
                  aria-hidden="true"
                >
                  {isMet ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </span>
                <span className={isMet ? 'font-medium' : ''}>
                  {req.label}
                </span>
                <span className="sr-only">
                  {isMet ? t('profile:security.requirements.met') : t('profile:security.requirements.notMet')}
                </span>
              </li>
            )
          })}
        </ul>

        {/* Additional Feedback */}
        {strength >= 4 && (
          <div className="mt-3 pt-3 border-t border-green-300">
            <p className="text-xs text-green-700 flex items-center">
              <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              {t('profile:security.strength.excellent')}
            </p>
          </div>
        )}

        {strength <= 2 && password.length > 0 && (
          <div className="mt-3 pt-3 border-t border-red-300">
            <p className="text-xs text-red-700 flex items-center">
              <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {t('profile:security.strength.improvement')}
            </p>
          </div>
        )}
      </div>

      {/* Password Tips (shown for weak passwords) */}
      {strength <= 2 && password.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs font-medium text-blue-800 mb-1">
            {t('profile:security.tips.improve')}
          </p>
          <ul className="space-y-0.5 text-xs text-blue-700">
            <li>• {t('profile:security.tips.mix')}</li>
            <li>• {t('profile:security.tips.avoid')}</li>
            <li>• {t('profile:security.tips.length')}</li>
          </ul>
        </div>
      )}
    </div>
  )
}

export default PasswordStrengthIndicator